import { useEffect, useMemo, useRef, useState } from "react";
import { lineupSize } from "../../constants";
import { Page, Uid } from "../../types";
import { currencyFormatter, pointsFormatter } from "../../helpers";
import {
  EnrichedArtist,
  SearchResult,
  isSearchResultArtist,
} from "../../api/types";

import LineupPager from "./LineupPager";
import PlayerLineup from "./PlayerLineup";
import CurrentLineupButtons from "./CurrentLineupButtons";
import NextLineupButtons from "./NextLineupButtons";
import SearchModal from "../SearchModal";
import ArtistModal from "../ArtistModal";
import AppPage from "../AppPage";
import MenuBottomBar, { MenuBarItem } from "../MenuBottomBar";
import PlayerHeaderComponent from "../PlayerHeaderComponent";
import { globals } from "../../styles";
import styles from "./MainPage.module.css";
import { useLineupCache } from "../../hooks/useLineup";
import { arrow_Icon, search_Icon } from "../../assets/icons";
import { images } from "../../styles";
import { useCurrentDraft } from "../../hooks/drafts";
import { useUserId } from "../../hooks/useUserId";
import LoadingSpinner from "../LoadingSpinner";
import { useCurrentPlayer, usePlayerStats } from "../../hooks/player";
import { useNavigate } from "../../hooks/useNavigate";
import {
  useCurrentLineup,
  useCurrentLineupIndex,
  useNextWeekLineup,
} from "../../hooks/lineups";
import {
  useGetArtistPrices,
  useGetArtists,
  useGetArtistsPoints,
} from "../../hooks/artists";
import FullscreenModal from "../FullscreenModal";

//ELLE TODO FROM DIYANKA: ADD STAGE/LIST VIEW TOGGLE FUNCTIONALITY TO LineupStageListToggle.tsx
// COMPONENT FOR STAGE VIEW IS CALLED ARTISTSTAGECOMPONENT
function MainPage(props: { handleBackButton: () => void }) {
  const { handleBackButton } = props;
  const { currentLineupIndex: lineupIndex } = useCurrentLineupIndex();
  const { navigate } = useNavigate();
  const { data: playerDetails, isPending: isUserDataPending } =
    useCurrentPlayer();
  const { data: currentDraft } = useCurrentDraft();
  const userId = useUserId();
  const { data: playerStats, isPending: isStatsLoading } =
    usePlayerStats(userId);

  const { data: currentLineup, isPending: isCurrentLineupLoading } =
    useCurrentLineup(userId, {
      draftId: currentDraft?.draftId,
      lineupIndex,
    });

  const { data: nextWeekLineup, isPending: isNextWeekLineupLoading } =
    useNextWeekLineup(userId, {
      draftId: currentDraft?.draftId,
      lineupIndex,
    });

  const playerWorldRank = useMemo(() => {
    if (!currentDraft?.leaderboardGroupId || !playerStats) {
      return -1;
    }
    return (
      playerStats.ranks.find(
        (rank) => rank.groupId === currentDraft.leaderboardGroupId,
      )?.rank ?? -1
    );
  }, [currentDraft, playerStats]);
  const {
    state: lineupCacheState,
    pendingChanges,
    syncError,
    sync,
    recruitArtist,
    boostArtist,
    dropArtist,
    removeBoost,
    resetCache,
  } = useLineupCache(userId, {
    draftId: currentDraft?.draftId,
    lineupIndex,
  });
  const [lineupError, setLineupError] = useState<string>();
  const prevLineupCacheStateRef = useRef(lineupCacheState);
  useEffect(() => {
    const lineupCacheChanged =
      prevLineupCacheStateRef.current !== lineupCacheState;
    prevLineupCacheStateRef.current = lineupCacheState;
    if (syncError && !lineupCacheChanged) {
      switch (syncError.status) {
        case 403:
          setLineupError(`This draft has ended. Please refresh your browser.`);
          break;
        case 409:
          setLineupError(
            `The server could not validate your lineup. Please try again.`,
          );
          break;
        case 503:
          setLineupError(
            `The server is still processing data. Please try again later.`,
          );
          break;
        case 500:
        case 400:
        case 401:
        default:
          setLineupError(`Unhandled error. Please refresh your browser.`);
          break;
      }
    } else {
      setLineupError(undefined);
    }
  }, [syncError, pendingChanges, lineupCacheState]);

  // Derive artist IDs that were added locally but aren't in the server's next lineup
  const serverNextLineupIds = useMemo(
    () => new Set((nextWeekLineup?.artists ?? []).map((a) => a.id)),
    [nextWeekLineup],
  );
  const newlyAddedIds = useMemo(
    () =>
      (lineupCacheState?.lineup ?? []).filter(
        (id) => !serverNextLineupIds.has(id),
      ),
    [lineupCacheState, serverNextLineupIds],
  );

  const newArtistQueries = useGetArtists(newlyAddedIds);
  const newArtistPriceQueries = useGetArtistPrices(
    newlyAddedIds,
    currentDraft?.draftId,
  );
  const newArtistPointsQueries = useGetArtistsPoints(newlyAddedIds, {});

  // Build the display list for the next lineup by applying cache changes to server data
  const nextLineupArtists = useMemo<EnrichedArtist[]>(() => {
    if (!lineupCacheState) return [];
    const cacheIds = new Set(lineupCacheState.lineup);
    const serverArtists = (nextWeekLineup?.artists ?? []).filter((a) =>
      cacheIds.has(a.id),
    );
    const addedArtists: EnrichedArtist[] = newlyAddedIds
      .map((_, i) => {
        const base = newArtistQueries[i]?.data;
        const price = newArtistPriceQueries[i]?.data;
        const points = newArtistPointsQueries[i]?.data;
        if (!base || !price || !points) return undefined;
        return { ...base, artistPrice: price, artistPoints: points };
      })
      .filter((a): a is EnrichedArtist => a !== undefined);
    return [...serverArtists, ...addedArtists];
  }, [
    lineupCacheState,
    nextWeekLineup,
    newlyAddedIds,
    newArtistQueries,
    newArtistPriceQueries,
    newArtistPointsQueries,
  ]);

  const [currentArtistId, setCurrentArtistId] = useState<Uid | undefined>(
    undefined,
  );

  const [isViewingCurrentLineup, setIsViewingCurrentLineup] = useState(true);
  const isNewLineup = (currentLineup?.artists.length ?? 0) === 0;
  const showingCurrentLineup = isViewingCurrentLineup && !isNewLineup;
  const [showSearch, setShowSearch] = useState(false);

  const sumPointsForLineup = useMemo(() => {
    if (showingCurrentLineup) {
      if (!currentLineup) return "Lineup Null";
      return pointsFormatter(
        currentLineup.artists.reduce(
          (sum, artist) => sum + artist.artistPoints.totalPoints,
          0,
        ),
      );
    } else {
      return pointsFormatter(
        nextLineupArtists.reduce(
          (sum, artist) => sum + artist.artistPoints.totalPoints,
          0,
        ),
      );
    }
  }, [currentLineup, showingCurrentLineup, nextLineupArtists]);

  const sumCostForLineup = useMemo(() => {
    if (showingCurrentLineup) {
      if (!currentLineup) return "Lineup Null";
      return currencyFormatter(
        currentLineup.artists.reduce(
          (sum, artist) => sum + (artist.artistRecruitmentPrice ?? 0),
          0,
        ),
      );
    } else {
      return currencyFormatter(
        nextLineupArtists.reduce(
          (sum, artist) => sum + artist.artistPrice.currentPrice,
          0,
        ),
      );
    }
  }, [currentLineup, showingCurrentLineup, nextLineupArtists]);

  // --- End Hooks ---
  const { lineup, boostedArtist, money, trades } = lineupCacheState ?? {
    lineup: [] as string[],
    boostedArtist: undefined,
    money: 0,
    trades: 0,
  };

  const isCurrentArtistInNextLineUp =
    !!currentArtistId && lineup.includes(currentArtistId);

  // --- Cache Functions ---

  const handleCloseArtistAndViewNextLineup = () => {
    handleCloseArtistPage();
    setIsViewingCurrentLineup(false);
  };

  const handleAddToLineup = (artistId: Uid) => {
    recruitArtist(artistId).then((result) => {
      if (result.success) {
        handleCloseArtistAndViewNextLineup();
      }
    });
  };
  const handleBoost = (artistId: Uid | undefined) => {
    if (artistId) {
      boostArtist(artistId);
    } else {
      removeBoost();
    }
  };
  const handleDropFromLineup = (artistId: Uid) => {
    dropArtist(artistId).then((result) => {
      if (result.success) {
        handleCloseArtistAndViewNextLineup();
      }
    });
  };

  // --- UI Functions ---
  const handleArtistModalBoost = () => {
    if (currentArtistId) {
      boostArtist(currentArtistId);
      handleCloseArtistAndViewNextLineup();
    }
  };

  const handleArtistResultClicked = (a: SearchResult) => {
    if (!isSearchResultArtist(a)) return;
    setCurrentArtistId(a.artistId);
  };

  const handleCloseArtistPage = () => {
    setShowSearch(false);
    setCurrentArtistId(undefined);
  };

  const handleCancelNewLineup = () => {
    resetCache();
    setIsViewingCurrentLineup(true);
  };

  const canAddArtistToLineup = (res: SearchResult): boolean => {
    if (!isSearchResultArtist(res) || !res.currentPrice) return false;
    return money - res.currentPrice >= 0 && !lineup.includes(res.artistId);
  };

  const handleCurrentLineupClicked = () => setIsViewingCurrentLineup(true);
  const handleNextLineupClicked = () => setIsViewingCurrentLineup(false);

  const handleSearchOpened = () => setShowSearch(true);

  function renderError() {
    if (!lineupError) return null;
    return (
      <FullscreenModal onClose={() => setLineupError(undefined)}>
        <p className={globals["user-form__error"]}>{lineupError}</p>
      </FullscreenModal>
    );
  }

  if (
    isUserDataPending ||
    !playerDetails ||
    isStatsLoading ||
    !playerStats ||
    isCurrentLineupLoading ||
    !currentLineup ||
    isNextWeekLineupLoading ||
    !nextWeekLineup ||
    !lineupCacheState
  ) {
    return <LoadingSpinner />;
  }

  return (
    <AppPage
      className={`${globals["page"]}`}
      bottomBar={
        <MenuBottomBar
          items={[
            MenuBarItem.Home,
            MenuBarItem.News,
            MenuBarItem.Leaderboards,
            MenuBarItem.Groups,
            MenuBarItem.LogOut,
          ]}
        />
      }
      style={{ justifyContent: "flex-start" }}
    >
      <button
        className={`${globals["button-wrapper"]} ${globals["back-btn--top-left"]}`}
        onClick={handleBackButton}
      >
        <div className={globals["back-btn"]}>
          <img src={arrow_Icon} alt="" className={images["back-arrow-icon"]} />
        </div>
      </button>
      {currentArtistId && (
        <ArtistModal
          currentArtistId={currentArtistId}
          isCurrentArtistInLineUp={isCurrentArtistInNextLineUp}
          isLineupFull={lineup.length >= lineupSize}
          handleDropFromLineup={() => handleDropFromLineup(currentArtistId)}
          // TODO - Let player add artists from current lineup
          handleAddToLineup={
            showingCurrentLineup
              ? undefined
              : () => handleAddToLineup(currentArtistId)
          }
          handleBackToSearch={
            showSearch ? () => setCurrentArtistId(undefined) : undefined
          }
          handleCloseArtistPage={handleCloseArtistPage}
          handleArtistBoost={
            currentArtistId === boostedArtist
              ? undefined
              : handleArtistModalBoost
          }
        />
      )}
      {showSearch && (
        <SearchModal
          searchType="artist"
          handleResultClicked={handleArtistResultClicked}
          handleSearchClosed={() => setShowSearch(false)}
          hideModal={!!currentArtistId}
          validationFunction={canAddArtistToLineup}
        />
      )}
      <PlayerHeaderComponent
        playerName={playerDetails.username}
        totalPoints={playerStats.totalScore}
        globalRank={playerWorldRank}
        freeTrades={trades}
      />
      <button
        className={globals["button-wrapper"]}
        onClick={handleSearchOpened}
        style={{ width: "100%" }}
      >
        <div className={styles["search-trigger"]}>
          <img
            src={search_Icon}
            alt=""
            className={images["search-trigger-icon"]}
          />
          <span className={styles["search-trigger-text"]}>
            Search for Artists!
          </span>
        </div>
      </button>
      {/* TODO: Add in Stage View <LineupStageListToggle /> */}
      <LineupPager
        handleCurrentLineupClicked={handleCurrentLineupClicked}
        handleNextLineupClicked={handleNextLineupClicked}
        isViewingCurrentLineup={isViewingCurrentLineup}
        gameweekLabel={`Game Week ${currentDraft?.currentWeek}, Next Deadline ${currentDraft?.nextDeadline}`}
        isNewLineup={isNewLineup}
      />
      <PlayerLineup
        artistLineup={
          showingCurrentLineup ? currentLineup.artists : nextLineupArtists
        }
        boostedArtist={
          showingCurrentLineup
            ? (currentLineup.boostedArtistId ?? undefined)
            : boostedArtist
        }
        formattedPointsTotal={sumPointsForLineup}
        formattedCostTotal={sumCostForLineup}
        formattedMoneyTotal={currencyFormatter(
          showingCurrentLineup ? currentLineup.budget : money,
        )}
        handleOpenArtistPage={(id) => setCurrentArtistId(id)}
        handleDropFromLineup={
          showingCurrentLineup ? undefined : handleDropFromLineup
        }
        handleArtistBoost={showingCurrentLineup ? undefined : handleBoost}
        handleSearchOpened={handleSearchOpened}
        tradesRemaining={trades}
      />
      {showingCurrentLineup ? (
        <>
          <CurrentLineupButtons
            handleNewsClicked={() => navigate(Page.News)}
            handleLeaderboardClicked={() => navigate(Page.LeaderboardSelection)}
            handleHowToPlay={() => navigate(Page.HowToPlay)}
            handleSearchOpened={handleSearchOpened}
          />
        </>
      ) : (
        <NextLineupButtons
          hasLineupChanged={pendingChanges}
          canConfirmLineup={lineup.length === lineupSize}
          handleCancelNewLineupClicked={handleCancelNewLineup}
          handleConfirmNewLineupClicked={sync}
          handleSearchOpened={handleSearchOpened}
        />
      )}
      {renderError()}
    </AppPage>
  );
}

export default MainPage;
